import { useQuery } from '@tanstack/react-query';
import { useHybridAuth } from './useHybridAuth';
import { useDriverProfile } from './useDriverProfile';
import { QueryConfig } from '../api/client';
import { driverApi } from '../services/driverApi';

const getVideoPermissions = async (token: string, companyId: string): Promise<{ canUploadVideo: boolean }> => {
  driverApi.setAuthToken(token);
  return driverApi.getVideoPermissions(companyId);
};

export const useVideoPermissions = (
  companyId?: string,
  config?: QueryConfig<typeof getVideoPermissions>
) => {
  const { getAccessTokenSilently, isAuthenticated } = useHybridAuth();
  const { data: profile } = useDriverProfile();

  const resolvedCompanyId = companyId ?? profile?.companyId;

  return useQuery({
    ...config,
    queryFn: async () => {
      if (!resolvedCompanyId) throw new Error('Company ID not available');
      const token = await getAccessTokenSilently();
      return getVideoPermissions(token, resolvedCompanyId);
    },
    queryKey: ['video-permissions', resolvedCompanyId],
    enabled: isAuthenticated && !!resolvedCompanyId && (config?.enabled !== false),
    staleTime: 24 * 60 * 60 * 1000,
    refetchInterval: 24 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};
